import { useEffect, useRef, RefObject } from 'react';

/**
 * Focusable selector — covers the standard set of elements that should
 * participate in keyboard tab order. `[tabindex]:not([tabindex="-1"])`
 * catches custom focusables (e.g. styled divs with `tabIndex={0}`).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** What gets focus when the trap opens: somewhere to type, never a button. */
const FIELD_SELECTOR = [
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"])',
  'select:not([disabled])',
].join(',');

/**
 * The open traps, oldest first. Only the last one - the modal on top - answers
 * the keyboard: every trap listens on `document`, and without this one Escape
 * closed a confirmation AND the form under it, and Shift+Tab in the top modal
 * let the one beneath pull focus down into itself.
 */
const openTraps: HTMLElement[] = [];

type FocusTrapOptions = {
  /**
   * Whether the trap is active. When `false`, the hook is a no-op —
   * useful for modals that mount/unmount: pass `isOpen`.
   */
  active: boolean;
  /**
   * Optional ESC-key handler. Fires when the user presses Escape while
   * focus is anywhere inside the trapped container. Typically wired to
   * `closeModal`.
   */
  onEscape?: () => void;
};

/**
 * Lightweight focus trap + ESC handler + focus restoration.
 *
 * What it does on activation:
 *   1. Records `document.activeElement` so it can be restored on close
 *   2. Moves focus to the first form field in `containerRef`, or to the
 *      container itself when there is none
 *   3. Intercepts Tab / Shift+Tab inside the container and cycles
 *      focus so it never escapes (the standard modal pattern)
 *   4. Intercepts Escape and calls `onEscape`
 *
 * On deactivation (or unmount), it restores focus to the element that
 * was active before the trap started — so dismissing a modal returns
 * the user to the trigger that opened it. Good for keyboard users and
 * screen readers.
 *
 * No deps installed: native DOM only, ~30 lines of real logic. If we
 * later need richer behavior (e.g. handling shadow DOM, iframes,
 * `inert`), swap in `focus-trap-react`.
 *
 * @example
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, { active: isOpen, onEscape: closeModal });
 *   return <div ref={ref} role="dialog" aria-modal>...</div>
 */
export function useFocusTrap<T extends HTMLElement>(
  containerRef: RefObject<T>,
  { active, onEscape }: FocusTrapOptions
): void {
  // Keep the latest `onEscape` in a ref so the keydown listener can call
  // the current handler without `onEscape` being a dependency of the main
  // effect below. Callers typically pass an inline `() => closeModal()`,
  // whose identity changes on every parent render — if that re-ran the main
  // effect, its "focus first element" step would yank focus back to the
  // first input on every keystroke.
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Remember what to return focus to when the trap deactivates.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the modal: the first field to type into, else the
    // container itself (which needs tabIndex={-1} to be programmatically
    // focusable). Never the first button - with no field before it that is
    // the close X or the modal's action, and a button focused from script
    // on open wears the :focus-visible ring as if it had been tabbed to.
    // Tab still reaches every button from the container. Done in rAF so
    // transitions / portals have a tick to settle.
    const raf = requestAnimationFrame(() => {
      const field = container.querySelector<HTMLElement>(FIELD_SELECTOR);
      // A select first is not somewhere to type, and focused from script it
      // wears its focus border as if picked: the container takes it instead.
      if (field && field.tagName !== 'SELECT') field.focus();
      else container.focus();
    });

    openTraps.push(container);

    const onKeyDown = (e: KeyboardEvent) => {
      if (openTraps[openTraps.length - 1] !== container) return;

      if (e.key === 'Escape' && onEscapeRef.current) {
        e.stopPropagation();
        onEscapeRef.current();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null); // skip visually-hidden

      if (focusables.length === 0) {
        // Nothing focusable inside — keep focus pinned on the container
        e.preventDefault();
        container.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        // Shift+Tab on first (or on the container, where focus starts
        // when there is no field) → wrap to last
        if (
          activeEl === first ||
          activeEl === container ||
          !container.contains(activeEl)
        ) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab on last → wrap to first
        if (activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      const index = openTraps.lastIndexOf(container);
      if (index !== -1) openTraps.splice(index, 1);
      // Restore focus to whatever opened the modal — but only if that
      // element is still in the DOM and not the body (which means we'd
      // just blur away from nothing useful).
      if (
        previouslyFocused &&
        previouslyFocused !== document.body &&
        document.body.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
    // `onEscape` intentionally omitted — handled via `onEscapeRef` so an
    // unstable inline handler doesn't re-run this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, containerRef]);
}
